import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { SetupStructureProfitCenterStore } from '../../../store';
import { HttpService, TreeService, BreadcrumbService, BusinessService, ConstantService } from '../../../shared/services';

@Component({
    selector: 'app-consolidated-report',
    templateUrl: './consolidated-report.component.html',
    styleUrls: ['./consolidated-report.component.scss'],
})
export class ConsolidatedReportComponent implements OnInit, OnDestroy {
    reportType: 'summary' | 'detailed' = 'detailed';
    startDate: Date | null = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; })();
    endDate: Date | null = new Date();
    profitCenters: any[] = [];
    selectedPCtrID: string = 'ALL';
    storeID: string = '';
    storeName: string = '';
    isLoading: boolean = false;
    isGenerated: boolean = false;
    errorMessage: string = '';
    loadingProgress: string = '';
    isPdfLoading: boolean = false;
    isCsvLoading: boolean = false;

    summaryRows: { name: string; revenue: number; expenses: number; net: number }[] = [];
    detailedGroups: { pctrName: string; txns: any[]; subtotal: any }[] = [];

    @ViewChild('reportResults') reportResults: ElementRef;

    bsConfig = { dateInputFormat: 'MM/DD/YYYY', containerClass: 'theme-default' };

    private storeSub: Subscription;

    constructor(
        private pctrStore: SetupStructureProfitCenterStore,
        private httpService: HttpService,
        private treeService: TreeService,
        private breadcrumbService: BreadcrumbService,
        private businessService: BusinessService,
        private constantService: ConstantService,
    ) {}

    ngOnInit(): void {
        this.setBreadcrumb();
        this.storeSub = this.treeService.watchStoreID$.subscribe((id: string) => {
            this.storeID = id || '';
            this.isGenerated = false;
            this.summaryRows = [];
            this.detailedGroups = [];
            this.errorMessage = '';
            if (id) {
                this.loadStoreName(id);
                this.loadProfitCenters(id);
            } else {
                this.storeName = '';
                this.profitCenters = [];
                this.selectedPCtrID = 'ALL';
            }
        });
    }

    ngOnDestroy(): void {
        if (this.storeSub) {
            this.storeSub.unsubscribe();
        }
    }

    setBreadcrumb(): void {
        const breadCrumb: any = {};
        breadCrumb.root = 'Reports';
        breadCrumb.child = 'Consolidated Report';
        breadCrumb.subChild = '';
        breadCrumb.subChildLink = null;
        breadCrumb.grandChild = '';
        breadCrumb.rootList = [];
        this.breadcrumbService.setBreadCrumb(breadCrumb);
    }

    loadStoreName(storeID: string): void {
        this.httpService.get('xbcStores', storeID).subscribe(
            (res: any) => { this.storeName = res?.data?.xName || ''; },
            () => { this.storeName = ''; },
        );
    }

    loadProfitCenters(storeID: string): void {
        this.pctrStore.getAll({ xStoreID: storeID }).subscribe(
            (res: any) => {
                this.profitCenters = (res?.data || []).filter((p: any) => p.xStatus === 1);
                if (!this.selectedPCtrID) {
                    this.selectedPCtrID = 'ALL';
                }
            },
            () => { this.profitCenters = []; },
        );
    }

    get profitCentersWithAll(): any[] {
        return [{ _id: 'ALL', xName: 'ALL' }, ...this.profitCenters];
    }

    get dateRangeInvalid(): boolean {
        if (!this.startDate || !this.endDate) { return false; }
        const diffMs = new Date(this.endDate).getTime() - new Date(this.startDate).getTime();
        return diffMs < 0 || diffMs > 256 * 24 * 60 * 60 * 1000;
    }

    generateReport(): void {
        if (this.dateRangeInvalid) { return; }
        this.errorMessage = '';
        this.isGenerated = false;
        this.summaryRows = [];
        this.detailedGroups = [];
        if (this.reportType === 'summary') {
            this.generateSummary();
        } else {
            this.generateDetailed();
        }
    }

    generateSummary(): void {
        this.isLoading = true;
        const payload: any = {
            xStoreID: this.storeID,
            reportType: 'summary',
            startDate: this.startDate,
            endDate: this.endDate,
        };
        if (this.selectedPCtrID !== 'ALL') {
            payload.xPCtrID = this.selectedPCtrID;
        }
        // TODO: replace 'consolidated-report' with actual endpoint when backend is ready
        this.httpService.store('consolidated-report', payload).subscribe(
            (res: any) => {
                this.isLoading = false;
                this.summaryRows = res?.data || [];
                this.isGenerated = true;
            },
            () => {
                this.isLoading = false;
                this.errorMessage = 'Report generation failed. Please try again.';
            },
        );
    }

    generateDetailed(): void {
        this.isLoading = true;
        const params: any = { hashData: true, filterField: 'xSettleDate',filterType:'advancedDateFilter' };
        const businessID = this.businessService.businessID.getValue();
        if (businessID) { params.xBusinessID = businessID; }
        if (this.selectedPCtrID === 'ALL') {
            params.xStoreID = this.storeID;
        } else {
            params.xPCtrID = this.selectedPCtrID;
        }
        
        if (this.startDate) { params.startDate = this.startDate; }
        if (this.endDate) { params.endDate = this.endDate; }

        this.loadingProgress = 'Loading...';
        this.fetchAllPages(params, 1, []);
    }

    private fetchAllPages(baseParams: any, page: number, accumulated: any[]): void {
        this.httpService.getAll('xpeSettledTxns', { ...baseParams, page }).subscribe(
            (res: any) => {
                const batch: any[] = res?.data || [];
                const total: number = res?.meta?.totalRecords || 0;
                const combined = accumulated.concat(batch);

                if (batch.length > 0 && combined.length < total) {
                    this.loadingProgress = `Loaded ${combined.length} / ${total} records, please wait...`;
                    this.fetchAllPages(baseParams, page + 1, combined);
                } else {
                    const groups = this.groupByPctr(combined);
                    this.constantService.updateTransactionData(combined).then(() => {
                        this.isLoading = false;
                        this.loadingProgress = '';
                        this.detailedGroups = groups;
                        this.isGenerated = true;
                    });
                }
            },
            () => {
                this.isLoading = false;
                this.loadingProgress = '';
                this.errorMessage = 'Report generation failed. Please try again.';
            },
        );
    }

    groupByPctr(txns: any[]): { pctrName: string; txns: any[]; subtotal: any }[] {
        const map = new Map<string, { pctrName: string; txns: any[]; subtotal: any }>();
        txns.forEach((t: any) => {
            const key = t.xPCtrID || 'unknown';
            if (!map.has(key)) {
                map.set(key, { pctrName: t.xPCtrName || t.pctrName || key, txns: [], subtotal: null });
            }
            map.get(key).txns.push(t);
        });
        map.forEach((group) => {
            group.subtotal = {
                xMainAmt: this.sumField(group.txns, 'xMainAmt')*0.01,
                xIncSurcAmt: this.sumField(group.txns, 'xIncSurcAmt')*0.01,
                xTipAmt: this.sumField(group.txns, 'xTipAmt')*0.01,
                xTtlAmt: this.sumField(group.txns, 'xTtlAmt')*0.01,
            };
            group.subtotal.xMerAmt = group.subtotal.xTtlAmt - group.subtotal.xIncSurcAmt;
        });
        return [...map.values()];
    }

    sumField(arr: any[], field: string): number {
        return arr.reduce((s, item) => s + (parseFloat(item[field]) || 0), 0);
    }

    getSummaryGrandTotal(): { revenue: number; expenses: number; net: number } {
        return {
            revenue: this.summaryRows.reduce((s, r) => s + (r.revenue || 0), 0),
            expenses: this.summaryRows.reduce((s, r) => s + (r.expenses || 0), 0),
            net: this.summaryRows.reduce((s, r) => s + (r.net || 0), 0),
        };
    }

    getDetailedGrandTotal(): any {
        return {
            xMainAmt:    this.detailedGroups.reduce((s, g) => s + (g.subtotal.xMainAmt   || 0), 0),
            xIncSurcAmt: this.detailedGroups.reduce((s, g) => s + (g.subtotal.xIncSurcAmt || 0), 0),
            xTipAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xTipAmt     || 0), 0),
            xTtlAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xTtlAmt     || 0), 0),
            xMerAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xMerAmt     || 0), 0),
        };
    }

    exportPdf(): void {
        this.isPdfLoading = true;
        const domClone = this.reportResults.nativeElement.cloneNode(true);
        let printSection = document.getElementById('printSection');
        if (!printSection) {
            printSection = document.createElement('div');
            printSection.id = 'printSection';
            document.body.appendChild(printSection);
        }
        printSection.innerHTML = '';
        printSection.appendChild(domClone);
        print();
        this.isPdfLoading = false;
    }

    exportCsv(): void {
        this.isCsvLoading = true;
        let csv = '';
        const filename = `consolidated-report-${this.storeName || this.storeID}.csv`;

        if (this.reportType === 'summary') {
            csv += 'Profit Center,Total Revenue,Total Expenses,Net Total\n';
            this.summaryRows.forEach(row => {
                csv += `"${row.name}",${row.revenue},${row.expenses},${row.net}\n`;
            });
            const gt = this.getSummaryGrandTotal();
            csv += `"TOTAL",${gt.revenue},${gt.expenses},${gt.net}\n`;
        } else {
            csv += 'Profit Center,Type,Invoice,Customer #,Card,Account #,Amt,SurChg,Tip,TtlAmt,MerAmt,Auth Date,Batch Date\n';
            this.detailedGroups.forEach(group => {
                group.txns.forEach((t: any) => {
                    csv += [
                        `"${group.pctrName}"`,
                        t.xTxnActTag || '',
                        t.xInvNum || '',
                        t.xCustNum || '',
                        t.xBrandTag || '',
                        t.xMaskedAcctNum || '',
                        t.xMainAmt || 0,
                        t.xIncSurcAmt || 0,
                        t.xTipAmt || 0,
                        t.xTtlAmt || 0,
                        t.xMerAmt || 0,
                        `"${t.xAuthDateTime || ''}"`,
                        `"${t.xSettleDateTime || ''}"`,
                    ].join(',') + '\n';
                });
                const s = group.subtotal;
                csv += `"${group.pctrName} - Subtotal",,,,,, ${s.xMainAmt},${s.xIncSurcAmt},${s.xTipAmt},${s.xTtlAmt},${s.xMerAmt},,\n`;
            });
            const gt = this.getDetailedGrandTotal();
            csv += `"GRAND TOTAL",,,,,,${gt.xMainAmt},${gt.xIncSurcAmt},${gt.xTipAmt},${gt.xTtlAmt},${gt.xMerAmt},,\n`;
        }

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', filename);
        a.click();
        URL.revokeObjectURL(url);
        this.isCsvLoading = false;
    }
}
